<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <Button
            v-if="getUserPermission('SELL_CASH_POSTING')"
            :label="$t('Post')"
            size="md"
            class="mt-0"
            @click="onPost"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="cashDepositList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(transactionDate)="data">
        {{ toDateWithTime(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <feather-icon
          icon="EyeIcon"
          class="cursor-pointer"
          @click="onViewSellCashToVault(data.item)"
        />
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Sell Cash To Vault')"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="sellCashFromVaultRules">
        <b-form>
          <b-row class="match-height">

            <!--  My Till Account -->
            <b-col
              md="6"
            >
              <InputField
                id="till-account-name"
                v-model="tillAccountNumber"
                :label="$t('My Till Account')"
                type="text"
                readonly
              />
            </b-col>
            <!-- My Till Account -->

            <!-- Vault Account -->
            <b-col
              md="6"
            >
              <SelectField
                id="vault-account"
                v-model="vaultAccountNumber"
                :label="$t('Vault Account')"
                :options="vaultAccountOptions"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                :loading="isLoadingVaultList"
                @input="setNarration(vaultAccountNumber)"
              />
            </b-col>
            <!-- Vault Account -->

            <!-- Amount -->
            <b-col
              md="12"
            >
              <InputNumberField
                id="amount"
                v-model="sellCashFromVaultFormData.amount"
                :label="$t('Amount')"
                :placeholder="$t('Enter amount')"
                :error-name="$t('Amount')"
                rules="positive"
              />
            </b-col>
            <!-- Amount -->

          </b-row>
          <b-row class="match-height">
            <b-col md="12">
              <TextAreaField
                id="narration"
                v-model="sellCashFromVaultFormData.narration"
                :label="$t('Narration')"
                :placeholder="$t('Enter narration')"
                :error-name="$t('Narration')"
                rules="required"
                for="narration"
                rows="3"
                readonly
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- View modal -->
    <b-modal
      id="modal-view"
      ref="modal-view"
      :ok-title="$t('Cancel')"
      ok-only
      :title="$t('Sell Cash To Vault')"
      ok-variant="outline-secondary"
      centered
      size="lg"
    >
      <div class="text-center">
        <h3> {{ $t('Posting Reference:') }} {{ sellCashFromVaultFormData.postingReferenceNumber }}</h3>
      </div>

      <TableEntries
        v-model="transactionPerPage"
        :options="perPageOptions"
      />

      <b-table
        ref="sellCashToVaultRef"
        :items="transactionList"
        responsive
        :busy="isBusy"
        :per-page="transactionPageSize"
        :current-page="transactionCurrentPage"
        :fields="translatedTransactionTableHeaders"
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        class="position-relative mt-3"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

        <template #cell(SN)="data">
          {{ getTableNumber(data.index) }}
        </template>
      </b-table>

      <TablePagination
        v-model="transactionCurrentPage"
        :from="transactionFrom"
        :to="transactionTo"
        :total-rows="transactionTotalEntries"
        :per-page="transactionPerPage"
      />

    </b-modal>
    <!-- View modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="modal-filter"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchSellCashToVault"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!--Posting Reference Number -->
        <b-col md="6">
          <InputField
            id="reference-number"
            v-model="filter.postingReferenceNumber"
            :label="$t('Reference Number')"
            :placeholder="$t('Enter posting reference number')"
            type="text"
          />
        </b-col>
        <!-- Posting Reference Number -->

        <!-- Instrument Number -->
        <b-col md="6">
          <InputField
            id="instrument-number"
            v-model="filter.instrumentNumber"
            :label="$t('Instrument Number')"
            :placeholder="$t('Enter instrument number')"
            type="text"
            for="instrument-number"
          />
        </b-col>
        <!-- Instrument Number -->
        <!-- GL code -->
        <b-col md="6">
          <InputField
            id="gl-code"
            v-model="filter.glCode"
            :label="$t('GL Code')"
            :placeholder="$t('Enter gl code')"
            type="text"
          />
        </b-col>
        <!-- GL code -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
          <!-- Branch -->
        </b-col>

        <!-- Financial Date From -->
        <b-col md="6">
          <DatePickerField
            id="date-of-public-holiday"
            v-model="filter.financialDateFrom"
            :label="$t('Financial Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Financial Date From-->

        <!-- Financial Date To -->
        <b-col md="6">
          <DatePickerField
            id="date-of-public-holiday"
            v-model="filter.financialDateTo"
            :label="$t('Financial Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Financial Date To-->

        <!-- Start Date -->
        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="filter.startDate"
            :label="$t('Start Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Start Date -->

        <!-- End Date -->
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="filter.endDate"
            :label="$t('End Date')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- End Date -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { required, positive } from '@validations'
import { v4 as uuidv4 } from 'uuid'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import { toAmount, toKobo, toBalance } from '@/helpers/formatAmount'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithTime } from '../../../../helpers/formatDate'
import InputField from '../../../forms/InputField.vue'
import TextAreaField from '../../../forms/TextAreaField.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import Button from '../../../components/button/Button.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import { postingPermissions } from '../../../../helpers/permissions'

const today = new Date()
const dateTime = moment(today).format()

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    TableEntries,
    BForm,
    BSpinner,
    BTable,
    ValidationObserver,
    InputNumberField,
    TablePagination,
    InputField,
    TextAreaField,
    SelectField,
    DatePickerField,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      showSpinner: false,
      required,
      positive,
      toDateWithYear,
      toDateWithTime,
      toAmount,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'initiatedBy', sortable: true, label: 'Initiated By' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'approvedBy', sortable: true, label: 'Approver' },
        { key: 'actions', label: 'Actions' },
      ],
      transactionTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
      ],
      categoryFormData: {
        categoryName: '',
        level: 'Select',
        specifyLevel: 'Select',
      },
      tillAccountNumber: '',
      tillAccountBranch: '',
      vaultAccountNumber: '',
      vaultAccountName: '',
      tillAccountName: '',
      isLoadingVaultList: false,
      sellCashFromVaultFormData: {
        amount: '',
        narration: '',
      },
      cashDepositList: [],
      sellCashToVaultData: {
        accountNumber: '',
        accountName: '',
        branch: '',
        financialDate: '',
        transactionDate: '',
        postingReferenceNumber: '',
        amount: '',
        entryCode: '',
        instrumentNumber: '',
        depositor: '',
        narration: '',
        approver: '',
      },
      postingFormData: {
        postingData: {
          realDate: dateTime,
          postingType: 'SellCashPosting',
          senderName: '',
          initiatedBy: '',
        },
      },
      posting: [],
      tillAccount: [],
      vaultAccount: [],
      filter: {
        accountName: null,
        accountNumber: null,
        postingReferenceNumber: null,
        instrumentNumber: null,
        branchCode: null,
        financialDateFrom: null,
        financialDateTo: null,
        startDate: null,
        endDate: null,
        entryCode: 'D-SCSH',
        glCode: null,
        postingType: 'SellCashPosting',
        recordType: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      transactionList: [],
      vaultAccountOptions: [],
      transactionPerPage: 10,
      transactionPageSize: 0,
      transactionCurrentPage: 1,
      transactionTotalCount: 0,
      postingReferenceNumber: null,
      vaultAccountBranch: '',
      // branchOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
      vaultAccounts: 'operations/getVaultAccount',
      tillAccounts: 'operations/getTillAccount',
      branchOptions: 'operations/branchOptions',
    }),
    transactionTotalEntries() {
      return this.transactionTotalCount
    },
    transactionNumberOfPages() {
      return Math.floor((this.transactionTotalEntries + this.transactionPerPage - 1) / this.transactionPerPage)
    },
    transactionFrom() {
      if (this.transactionTotalCount === 0) {
        return 0
      }
      return (this.transactionCurrentPage * this.transactionPerPage) - (this.transactionPerPage - 1)
    },
    transactionTo() {
      return Math.min(this.transactionFrom + this.transactionPerPage - 1, this.transactionTotalEntries)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTransactionTableHeaders() {
      return this.transactionTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchSellCashToVault()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchSellCashToVault()
    },
    transactionCurrentPage(newValue) {
      this.transactionCurrentPage = newValue
      this.fetchPostings(this.postingReferenceNumber)
    },
    transactionPerPage(newValue) {
      this.transactionPerPage = newValue
      this.fetchPostings(this.postingReferenceNumber)
    },
  },
  async mounted() {
    this.fetchSellCashToVault()
    this.fetchVaultAccounts()
    await this.getTillUserAccount()
  },
  methods: {
    ...mapActions({
      getPostingsReport: 'reports/getTransactionReports',
      postCashDeposit: 'posting/postingAction',
      getTillAccountList: 'operations/getTillAccountList',
      getVaultAccountList: 'operations/getVaultAccountList',
    }),
    getTableNumber(index) {
      return (this.transactionCurrentPage * this.transactionPerPage) - this.transactionPerPage + index + 1
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.sellCashFromVaultRules.validate().then(success => {
        if (success) {
          this.confirm(this.sellCashFromVaultAction, { text: this.$t('Are you sure you want to Sell Cash To Vault?') })
        }
      })
    },
    setNarration(_vaultAccountNumber) {
      const vaultAccount = this.vaultAccounts.find(x => x.accountNumber === _vaultAccountNumber)
      this.vaultAccountName = vaultAccount.accountName
      this.tillAccountName = this.tillAccounts.find(x => x.tellerUserID === this.userData.id)?.tellerName
      const text1 = this.$t('Sell Cash to')
      const text2 = this.$t('by')
      this.sellCashFromVaultFormData.narration = `${text1} ${vaultAccount.branchName} ${vaultAccount.accountName} - ${_vaultAccountNumber} ${text2} ${this.tillAccountName} - ${this.tillAccountNumber}`
    },
    // eslint-disable-next-line consistent-return
    async getVaultAccount() {
      try {
        const response = await this.getVaultAccountList({
          search: {
            branchId: this.userData.branchID,
          },
          permission: postingPermissions.VIEW_SELL_CASH_POSTING,
        })

        const { data, status } = response

        if (data.data !== null && status === 200) {
          this.vaultAccountBranch = data.data[0].branchCode
          return true
        }
        if (data.data == null && status === 200) {
          this.alertError({ text: this.$t('Vault Account has not been configured for your branch') })
          return false
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    // eslint-disable-next-line consistent-return
    async getTillUserAccount() {
      try {
        const response = await this.getTillAccountList({
          search: {
            tellerUserID: this.userData.id,
          },
          permission: postingPermissions.VIEW_SELL_CASH_POSTING,
        })
        const { data, status } = response

        if (data.data !== null && status === 200) {
          this.tillAccountNumber = data.data[0].accountNumber
          this.tillAccountBranch = data.data[0].branchCode
          return true
        }
        if (data.data == null && status === 200) {
          this.alertError({ text: this.$t('Till Account has not been configured for you') })
          return false
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async sellCashFromVaultAction() {
      try {
        this.isLoading = true
        const postingApprovalModel = {
          amount: Number(toKobo(this.sellCashFromVaultFormData.amount).toFixed()),
          initiatedBy: '',
          approvedBy: '',
          postingType: 'SellCashPosting',
        }
        this.postingFormData.postingApprovalModel = postingApprovalModel
        this.postingFormData.postingData.initiatedBy = this.userData.email
        const hasTillAccountConfigured = await this.getTillUserAccount()
        const hasVaultAccountConfigured = await this.getVaultAccount()

        if (hasTillAccountConfigured && hasVaultAccountConfigured) {
          this.postingFormData.postingEntry = [
            {
              accountNumber: this.tillAccountNumber,
              accountName: this.tillAccountName,
              amount: Number(toKobo(this.sellCashFromVaultFormData.amount).toFixed()),
              recordType: 'Credit',
              narration: this.sellCashFromVaultFormData.narration,
              instrumentNumber: `BC-${uuidv4()}`,
              entryCode: 'C-SCSH',
              branchCode: this.userData.branchCode,
            },
            {
              accountNumber: this.vaultAccountNumber,
              accountName: this.vaultAccountName,
              amount: Number(toKobo(this.sellCashFromVaultFormData.amount).toFixed()),
              recordType: 'Debit',
              narration: this.sellCashFromVaultFormData.narration,
              instrumentNumber: `BC-${uuidv4()}`,
              entryCode: 'D-SCSH',
              branchCode: this.vaultAccountBranch,
            }]

          const postingData = this.postingFormData
          const response = await this.postCashDeposit({ postingData, permission: postingPermissions.SELL_CASH_POSTING })

          const { status, data } = response
          if (status === 200 && data.isSuccessful) {
            this.hideModal('modal-lg')
            this.fetchSellCashToVault()
            this.alertSuccess({ text: data.message })
          }
          if (status === 200 && !data.isSuccessful) {
            this.hideModal('modal-lg')
            this.alertError({ text: data.message })
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async fetchVaultAccounts() {
      try {
        this.isLoadingVaultList = true
        const response = await this.getVaultAccountList({
          search: '',
          page: this.currentPage,
          limit: 200,
          permission: postingPermissions.VIEW_SELL_CASH_POSTING,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.vaultAccountOptions = data.data.map(item => ({ label: item.accountName, value: item.accountNumber }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingVaultList = false
      }
    },
    async fetchSellCashToVault() {
      try {
        this.isBusy = true
        const response = await this.getPostingsReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          route: 'postingsReport',
          permission: 'VIEW_SELL_CASH_POSTING',
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.cashDepositList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async fetchTillAccount() {
      try {
        this.showSpinner = true
        const response = await this.getTillAccountList({
          search: { tellerUserID: this.userData.id },
          permission: postingPermissions.VIEW_SELL_CASH_POSTING,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.tillAccount = data.data
          if (this.tillAccount === null) {
            this.alertError({ text: this.$t('Till Account not yet configured for this user') })
            return
          }
          this.tillAccountNumber = data.data[0].accountNumber
        } else {
          this.alertError({ text: this.$t('Till Account not yet configured for this user') })
          return
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async onPost() {
      try {
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchPostings(postingReferenceNumber) {
      const response = await this.getPostingsReport({
        search: { postingReferenceNumber },
        page: this.transactionCurrentPage,
        limit: this.transactionPerPage,
        route: 'postingsReport',
        permission: postingPermissions.VIEW_SELL_CASH_POSTING,
      })
      const { data, status } = response
      if (status === 200 && data.data) {
        this.transactionList = data.data.map(item => ({
          ...item,
          credit: toBalance(item.credit),
          debit: toBalance(item.debit),
        }))
        this.transactionTotalCount = data.recordCount
      }
    },
    async onViewSellCashToVault(items) {
      try {
        this.showSpinner = true
        this.postingReferenceNumber = items.postingReferenceNumber
        await this.fetchPostings(items.postingReferenceNumber)

        this.sellCashToVaultData = {
          ...items,
          transactionDate: this.toDateWithYear(items.transactionDate),
          financialDate: this.toDateWithYear(items.financialDate),
          credit: toBalance(items.credit),
        }
        this.$root.$emit('bv::show::modal', 'modal-view')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    resetFilters() {
      this.filter = {
        ...null,
        postingType: 'SellCashPosting',
        entryCode: 'D-SCSH',
      }
    },
    resetFilter() {
      this.filter = {
        ...null,
        postingType: 'SellCashPosting',
        entryCode: 'D-SCSH',
      }
      this.fetchSellCashToVault()
    },
    resetModal() {
      this.sellCashFromVaultFormData = {
        amount: '',
        vaultAccountNumber: '',
      }
    },
  },
}
</script>
